import axios from "axios";
import { Dialog, Notify, Platform } from "quasar";
import { defineStore } from "pinia";
import cpjPackage from "../../package.json";
import systemDialog from "../components/system/dialog";
import md5 from "js-md5";
import {
  FILE_EXCEL,
  GET_COFRE_CHAVE,
  GET_PREFERENCIAS,
  GET_TOKEN,
  LOGIN_API,
  PARAMETROS_API,
  SEND_MAIL,
} from "./lib/apis";
import systemMessages from "./lib/messages";
import { arraybufferToObject, generateCnonce, getAuthToken } from "./lib/utils";
import { useAuthStore } from "./auth";
import { useDashboardStore } from "./dashboard";

const ERRO_NaoLogado = 1;
const ERRO_TrocaSenha = 2;

export const useSystemStore = defineStore("system", {
  state: () => ({
    clientVersion: {
      sisCompilation: "2020.09.17.0",
    },
    isChangingPwd: false,
    isLogginOut: false,
    isReconnecting: false,
    menu: {
      icon: "",
      modulo: "",
      moreOptions: [],
      options: [],
      path: "",
      title: "",
    },
    module: "",
    parametros: {
      sisRegistrado: "sistema não registrado",
      sisNome: "",
      sisRg: "",
      sisNUsu: 0,
      sisVersao: "",
      sisCPFCNPJ: "",
      tablePerfil: [],
    },
    status: "ok",
    version: "H04.0.1",
  }),

  getters: {
    isLoading(state) {
      return state.status === "loading";
    },
    licVersaoClient() {
      return cpjPackage.version; //${state.parametros.sisVersao}.${state.clientVersion.sisCompilation}`
    },
    licVersaoServer(state) {
      return state.parametros?.SisFileVersaoServer;
    },
    menuCaption(state) {
      return state?.menu?.caption ?? "";
    },
    menuIcon(state) {
      return state?.menu?.icon ?? "";
    },
    menuModulo(state) {
      return state?.menu?.modulo ?? "";
    },
    menuMoreOptions(state) {
      return state?.menu?.moreOptions ?? [];
    },
    menuOnBack(state) {
      return state.menu?.onBack ?? null;
    },
    menuOptions(state) {
      return state?.menu?.options ?? [];
    },
    menuSearchOptions(state) {
      return state.menu?.searchOptions ?? null;
    },
    menuShowHeader(state) {
      return state.menu?.showHeader ?? undefined;
    },
    menuTitle(state) {
      return state?.menu?.title ?? "";
    },
    sisCPFCNPJ(state) {
      return state.parametros?.sisCPFCNPJ;
    },
    sisNome(state) {
      return state.parametros?.sisNome;
    },
    sisRg(state) {
      return state.parametros?.sisRg;
    },
  },

  actions: {
    clearMenu() {
      this.menu = {
        title: "",
        modulo: "",
        icon: "",
        path: "",
        options: [],
        searchOptions: null,
        filterOptions: null,
      };
    },
    clearStore() {
      // importa as outras stores
      const authStore = useAuthStore();
      const dashboardStore = useDashboardStore();

      this.isLogginOut = true;

      // auth
      authStore.clearAuth();
      // dashboard
      dashboardStore.clearDashboard();
      // system
      this.isReconnecting = false;
      this.parametros = {
        sisRegistrado: "sistema não registrado",
        sisNome: "",
        sisRg: "",
        sisNUsu: 0,
        sisVersao: "",
        sisCPFCNPJ: "",
        tablePerfil: [],
      };
      this.status = "ok";

      this.isLogginOut = false;
    },
    loadParams() {
      return new Promise((resolve, reject) => {
        this.post({ url: PARAMETROS_API, responseType: "text" })
          // carregou parâmetros
          .then((resp) => {
            // coloca os parâmetros no estado da aplicação
            for (let key in resp.data) {
              this.parametros[key] = resp.data[key];
            }
            resolve(resp);
          })
          // chamada falhou
          .catch((e) => {
            reject(e);
            return;
          });
      });
    },
    post(params) {
      return new Promise((resolve, reject) => {
        // importa as outras stores
        const authStore = useAuthStore();

        // retorna erro caso não receba url
        if (typeof params.url === "undefined") {
          reject({
            sysMsg: "missing-params",
            params: "stores/system.js -> post()",
          });
          return;
        }

        // se a chamada não tem setado um valor para tempo de espera, cria um
        if (!params.waitTimer) params.waitTimer = 0;

        // se o valor de espera ultrapassar 20 segundos, emite um erro de espera
        if (params.waitTimer > 20000) {
          reject({ sysMsg: "request-timeout", params: params.url });
          return;
        }

        // espera caso tenha uma chamada processando
        if (
          this.status === "loading" &&
          !this.isChangingPwd &&
          params.url !== LOGIN_API &&
          params.url !== GET_TOKEN
        ) {
          setTimeout(() => {
            params.waitTimer += 1;
            this.post(params)
              .then((resp) => resolve(resp))
              .catch((e) => reject(e));
          }, 1);
          return;
        }

        // altera o status do sistema
        this.status = "loading";

        // mapeia o usuário da aplicação para uma variável local
        let user = { ...authStore.user };

        // prepara os parâmetros da chamada
        let url = params.url;
        let data = params.data ?? "";
        let responseType = params.responseType ?? "text";
        authStore.setUserValues({ cNonce: generateCnonce() });
        if (data instanceof Object) data = JSON.stringify(data);

        // dispara a chamada
        axios
          .post(url, data, {
            responseType: responseType,
            headers: {
              "content-type": "text/plain",
              dashboard: getAuthToken(user, data, url),
              Authorization: "",
            },
          })
          // chamada bem sucedida
          .then((resp) => {
            // resposta vazia
            if (!resp.data) {
              reject({ sysMsg: "empty-response", params: url });
              this.status = "ok";
              return;
            }

            // validar se foi enviado pelo server
            if (
              url !== GET_TOKEN &&
              (url !== LOGIN_API || !user.ad) &&
              url !== GET_PREFERENCIAS
            ) {
              let hashDash = md5(
                user.token + ":" + user.ha1 + ":" + user.cNonce
              ).toUpperCase();
              if (hashDash !== resp?.headers?.dashboard) {
                reject({ sysMsg: "auth-error", params: url });
                this.status = "ok";
                // dispatch('auth/logout', null, { root: true })
                return;
              }
            }

            // verifica se é do tipo FILE_EXCEL
            if (url === FILE_EXCEL) {
              if (resp.data.byteLength > 0) {
                let headers = resp.headers;
                let blob = new Blob([resp.data], {
                  type: headers["content-type"],
                });
                let link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "dashboard.xlsx";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } else {
                reject({ sysMsg: "open-file-error" });
                return;
              }
            }

            resolve(resp);
            this.status = "ok";
          })
          // erro na chamada
          .catch((e) => {
            let response = e.response;
            // resposta vazia
            if (!response) {
              reject({ sysMsg: "empty-response", params: url });
              this.status = "ok";
              return;
            }

            // analisa o tipo de erro de retorno
            if (
              (response.status === 410 || response.status === 400) &&
              response.data
            ) {
              // verifica se precisa trocar a senha
              if (response.data.code === ERRO_TrocaSenha) {
                authStore.setUserAuthenticated(false);
                authStore
                  .trocarSenha()
                  .then(() => {
                    this.post(params)
                      .then((resp) => resolve(resp))
                      .catch((e) => reject(e));
                  })
                  .catch((e) => console.error(e));
                return;
              }
              // verifica se precisa reconectar
              if (response.data.code === ERRO_NaoLogado) {
                // começa a reconectar
                this.isReconnecting = true;
                let credentials = {
                  login: authStore.userLogin,
                  pwd: authStore.senha,
                };
                authStore
                  .login(credentials)
                  // caso seja bem sucedido
                  .then((resp) => {
                    // refaz a chamada
                    this.post(params)
                      .then((resp) => {
                        resolve(resp);
                      })
                      .catch((e) => {
                        reject(e);
                      })
                      .finally(() => {
                        this.isReconnecting = false;
                        this.status = "ok";
                        return;
                      });
                  })
                  .catch((errorLoginReconnect) => {
                    this.isReconnecting = false;
                    reject(errorLoginReconnect);
                    this.status = "ok";
                    return;
                  });
              }
              // caso contrário retorna o erro
              else {
                if (response.data instanceof ArrayBuffer) {
                  // verifica se a resposta veio como ArrayBuffer
                  try {
                    response.data = arraybufferToObject(response.data);
                  } catch (e) {}
                }

                if (response?.data?.error) {
                  // verifica se há erro
                  reject({
                    message: response.data.error,
                    icon: "las la-exclamation-circle",
                    color: "negative",
                    display: "notify",
                  });
                } else {
                  reject({ sysMsg: "invalid-response", params: url });
                }
                this.status = "ok";
                return;
              }
            }
            // verifica se
            else if (response.status === 504) {
              if (authStore.isLoggedIn) {
                authStore.logout();
              }
              reject({ sysMsg: "no-server-response" });
              this.status = "ok";
              return;
            }
            // caso contrário retorna o erro
            else {
              if (response?.data?.error) {
                reject({
                  message: response.data.error,
                  icon: "mdi-alert",
                  color: "negative",
                });
              } else {
                reject({ sysMsg: "invalid-response", params: url });
              }
              this.status = "ok";
              return;
            }
          });
      });
    },
    setIsChangingPwd(isChangingPwd = false) {
      if (typeof isChangingPwd != "boolean") return;
      this.isChangingPwd = isChangingPwd;
    },
    setMenu(payload) {
      if (typeof payload != "object") return;
      this.menu = payload;
    },
    setMenuTitle(payload) {
      if (typeof payload != "string") return;
      this.menu.title = payload;
    },
    setModule(payload) {
      this.module = payload;
    },
    setStatus(payload) {
      if (payload === "ok" || payload === "loading") {
        this.status = payload;
      }
    },
    showMessage(data) {
      // verifica se existem dados
      if (!data) return;

      //  verifica se é mensagem de sistema
      if (data.sysMsg) {
        // verifica se é uma mensagem de sistema válida
        if (systemMessages[data.sysMsg]) {
          this.showMessageInternal({
            ...systemMessages[data.sysMsg],
            params: data.params,
          });
        }
        return;
      }

      // caso contrário processa a mensagem normalmente
      this.showMessageInternal(data);
    },
    showMessageInternal(data) {
      // processa mensagens em formato de objeto
      if (typeof data === "object") {
        // verifica se é mensagem de erro do javascript
        if (data.stack && data.message) {
          data = {
            ...systemMessages["javascript-error"],
            params: `<br>${data.stack}`,
          };
        }

        // faz tradução dos argumentos na mensagem
        if (data.message.includes("$params")) {
          data.message = data.message.replace(
            "$params",
            `<b>${data.params}</b>`
          );
          data.html = true;
        }

        // mensagens de notificação
        if (!data.display || data.display === "notify") {
          Notify.create(data);
        }

        // mensagens de dialog
        if (data.display === "dialog") {
          Dialog.create({
            component: systemDialog,
            componentProps: data,
          });
        }
      }
      // exibe mensagem em forma de String
      if (typeof data === "string") {
        Notify.create(data);
      }
    },
  },
});
